import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/_core/hooks/useAuth";
import { useI18n } from "@/lib/i18n";
import { useActiveWorkspace } from "@/contexts/ActiveWorkspace";
import { trpc } from "@/lib/trpc";
import { weekStartUTC, weekKey } from "@shared/workweek";
import { X, Lock, Mic, Camera, AlertTriangle, Target } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { VoiceCaptureDialog } from "@/components/VoiceCaptureDialog";
import { PhotoCaptureDialog } from "@/components/PhotoCaptureDialog";
import { MyPrioritiesCard } from "@/components/home/MyPrioritiesCard";
import { BlockerCapture } from "./BlockerCapture";
import { CapturedReview } from "./CapturedReview";

const NOW = new Date();
const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WD_AR = ["أحد", "إثنين", "ثلاثاء", "أربعاء", "خميس", "جمعة", "سبت"];

type Panel = "blocker" | "priority";

/**
 * Capture sheet — a focused, vertical mobile action sheet (center-FAB modal, NOT
 * a route). PRIMARY path is a quick private note for the selected day; voice /
 * photo / blocker / priority are secondary. It feeds ONLY the private signal
 * pipeline — it never approves, shares, or sends anything to Team. Captured
 * activity only becomes shareable through the receipt approval preview later.
 *
 * Destination-day options come from the SERVER's `receipts.weekStatus.days`
 * (working days, local-tz). The client performs NO work-week classification.
 */
export function CaptureSheet({
  open,
  onOpenChange,
  initialDay,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** Preselect a destination day (YYYY-MM-DD), e.g. from a tapped week-strip day. */
  initialDay?: string;
}) {
  const { locale } = useI18n();
  const isAr = locale === "ar";
  const { isAuthenticated, user } = useAuth();
  const [, navigate] = useLocation();
  const ws = useActiveWorkspace();
  const teamId = ws.active?.team.id ?? null;

  const prefs = trpc.preferences.me.useQuery(undefined, { enabled: isAuthenticated && open });
  const weekStartDay = prefs.data?.weekStartDay ?? 0;
  const todayKey = new Date().toLocaleDateString("en-CA"); // local YYYY-MM-DD

  const enabled = isAuthenticated && teamId != null && open;

  const utils = trpc.useUtils();
  const setStatus = trpc.priorities.setStatus.useMutation({
    onSuccess: () => utils.priorities.forWeek.invalidate(),
  });
  const createSignal = trpc.signals.create.useMutation();

  const [selectedDay, setSelectedDay] = useState(initialDay ?? todayKey);
  // The sheet's week context FOLLOWS the selected destination day (e.g. opened
  // from an empty past/future week's "Capture update" action) — day chips and
  // the captured list show THAT week, not blindly the current one.
  const weekStart = useMemo(
    () => weekStartUTC(new Date(selectedDay + "T00:00:00.000Z"), weekStartDay),
    [selectedDay, weekStartDay],
  );
  const weekStartKey = useMemo(() => weekKey(weekStart, weekStartDay), [weekStart, weekStartDay]);

  const weekStatus = trpc.receipts.weekStatus.useQuery(
    { weekStartKey, workspaceTeamId: teamId },
    { enabled },
  );
  // Capture is PRIVATE and personal — scope to the caller's own priorities
  // (without forUserId a manager would get the whole team's rows here).
  const priorities = trpc.priorities.forWeek.useQuery(
    { teamId: teamId!, weekStartKey, forUserId: user?.id },
    { enabled: enabled && user?.id != null },
  );

  const [note, setNote] = useState("");
  const [panel, setPanel] = useState<Panel | null>(null);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);
  const noteRef = useRef<HTMLTextAreaElement | null>(null);

  // Keyboard-awareness via the VisualViewport API. On iOS Safari the on-screen
  // keyboard does NOT shrink `100dvh`, so a bottom sheet's lower half ends up
  // stuck BEHIND the keyboard. We track the visible viewport and (a) lift the
  // sheet to sit just above the keyboard, (b) cap its height to the visible
  // band so everything (Save, Captured this week) stays reachable by scrolling.
  const [kbInset, setKbInset] = useState(0);
  const [vpHeight, setVpHeight] = useState<number | null>(null);
  useEffect(() => {
    if (!open || typeof window === "undefined") return;
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      setKbInset(Math.max(0, window.innerHeight - vv.height - vv.offsetTop));
      setVpHeight(vv.height);
    };
    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
    };
  }, [open]);
  // Height for the sheet: above the keyboard, fill the visible band (small gap);
  // with no keyboard, keep the usual ~92% so the dimmed backdrop shows at top.
  const sheetMaxHeight =
    vpHeight == null ? undefined : Math.round(kbInset > 0 ? vpHeight - 8 : vpHeight * 0.92);

  // When the sheet opens (or a different day is requested), reset to that day
  // and a clean state.
  useEffect(() => {
    if (open) {
      setSelectedDay(initialDay ?? todayKey);
      setNote("");
      setPanel(null);
      // Put the cursor in the quick-update box on open so the user can type
      // immediately. iOS/Capacitor WebViews can ignore the `autoFocus` attr, so
      // we also focus via ref on the next frame (keeps the keyboard within the
      // opening gesture chain). The sheet is keyboard-aware, so this won't clip.
      requestAnimationFrame(() => noteRef.current?.focus());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialDay]);

  const workdays = (weekStatus.data?.days ?? []).filter(d => d.isWorkingDay);
  // Day options: the week's working days, plus the selected day if it isn't one
  // (e.g. a tapped weekend with a note), so the chosen day is always selectable.
  const dayKeys = useMemo(() => {
    const keys = workdays.map(d => d.dateKey);
    if (selectedDay && !keys.includes(selectedDay)) keys.unshift(selectedDay);
    if (keys.length === 0) keys.push(todayKey);
    return keys;
  }, [workdays, selectedDay, todayKey]);

  const fmt = (dateKey: string) =>
    new Date(dateKey + "T00:00:00.000Z").toLocaleDateString(isAr ? "ar-u-nu-latn" : "en", {
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    });
  const weekdayOf = (dateKey: string) =>
    (isAr ? WD_AR : WD)[new Date(dateKey + "T00:00:00.000Z").getUTCDay()];

  const onCaptured = () => {
    utils.signals.forWeek.invalidate();
    utils.receipts.weekStatus.invalidate();
    utils.receipts.forWeek.invalidate();
    utils.yourWeek.forWeek.invalidate();
  };

  const trimmed = note.trim();
  const saveUpdate = async () => {
    if (!trimmed) return;
    try {
      await createSignal.mutateAsync({
        title: trimmed.slice(0, 280),
        kind: "note",
        occurredOn: selectedDay,
        // Capture lands in the ACTIVE workspace (personal vs team), not
        // blindly the user's primary team — so a Personal capture is private
        // and shows in Personal, and a team capture is team-scoped.
        ...(teamId != null ? { workspaceTeamId: teamId } : {}),
        ...(trimmed.length > 280 ? { summary: trimmed.slice(0, 2000) } : {}),
      });
      setNote("");
      onCaptured();
      toast.success(
        isAr
          ? "حُفظ التحديث بشكل خاص. يمكنك الآن صياغة ملخّصك."
          : "Update saved privately. You can now draft your receipt.",
      );
    } catch {
      toast.error(
        isAr ? "تعذّر حفظ التحديث. حاول مرة أخرى." : "Couldn’t save your update. Please try again.",
      );
    }
  };

  if (!open) return null;

  // Voice is promoted to a co-primary action (mic in the Quick Update box), so
  // it's no longer duplicated here in the secondary grid.
  // `label` stays English — it seeds keys/testids. `labelAr` is display-only.
  const SECONDARY: { panel?: Panel; label: string; labelAr: string; icon: typeof Mic; onClick: () => void; active?: boolean }[] = [
    { panel: "blocker", label: "Blocker", labelAr: "معوق", icon: AlertTriangle, onClick: () => setPanel(p => (p === "blocker" ? null : "blocker")), active: panel === "blocker" },
    { panel: "priority", label: "Priority progress", labelAr: "تقدّم الأولويّات", icon: Target, onClick: () => setPanel(p => (p === "priority" ? null : "priority")), active: panel === "priority" },
    { label: "Photo", labelAr: "صورة", icon: Camera, onClick: () => setPhotoOpen(true) },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-end bg-black/30 print:hidden"
      style={{ bottom: kbInset }}
      onClick={() => {
        // Don't dismiss the sheet from a backdrop tap while a sub-dialog
        // (voice / photo) is open — its own picker can bubble odd events.
        if (voiceOpen || photoOpen) return;
        onOpenChange(false);
      }}
      data-testid="capture-sheet"
    >
      <div
        className="w-full max-h-[92dvh] overflow-y-auto overscroll-contain rounded-t-3xl bg-background px-5 pb-[calc(env(safe-area-inset-bottom)+1.5rem)]"
        style={sheetMaxHeight != null ? { maxHeight: `${sheetMaxHeight}px` } : undefined}
        onClick={e => e.stopPropagation()}
      >
        {/* Sticky header — stays pinned so the title/privacy line never slides
            behind the address bar when the body scrolls under the keyboard. */}
        <div className="sticky top-0 z-10 -mx-5 bg-background px-5 pt-[max(0.75rem,env(safe-area-inset-top))] pb-2">
          <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-muted-foreground/25" />
          <div className="flex items-start justify-between">
            <div>
              <h2 className="font-display text-xl leading-tight">{isAr ? "تسجيل تحديث" : "Capture update"}</h2>
              <p className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                <Lock className="h-3.5 w-3.5" /> {isAr ? "خاص بك حتى تعتمد ملخّصاً." : "Private to you until you approve a receipt."}
              </p>
            </div>
            <button aria-label={isAr ? "إغلاق" : "Close"} onClick={() => onOpenChange(false)} className="rounded-full p-1 hover:bg-muted">
              <X className="h-5 w-5 text-muted-foreground" />
            </button>
          </div>
        </div>

        {/* Destination day */}
        <div className="mt-4 mb-1.5 text-[11px] font-semibold tracking-wider text-muted-foreground">
          {isAr ? "اليوم المستهدف" : "DESTINATION DAY"}
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="capture-day-picker">
          {dayKeys.map(dk => {
            const active = dk === selectedDay;
            return (
              <button
                key={dk}
                onClick={() => setSelectedDay(dk)}
                data-testid="capture-day-option"
                className={cn(
                  "rounded-full border px-3 py-1 text-xs",
                  active ? "border-coral bg-coral-soft/60 text-coral" : "border-border text-muted-foreground",
                )}
              >
                {weekdayOf(dk)} {fmt(dk)}
                {dk === todayKey ? (isAr ? " · اليوم" : " · Today") : ""}
              </button>
            );
          })}
        </div>

        {/* PRIMARY: quick update — visible immediately, no mode picking. */}
        <div className="mt-4 flex items-center justify-between">
          <span className="text-[11px] font-semibold tracking-wider text-muted-foreground">
            {isAr ? "تحديث سريع" : "QUICK UPDATE"}
          </span>
          <span className="text-[11px] text-muted-foreground">{isAr ? "اكتب أو اضغط على المايك" : "Type or tap the mic"}</span>
        </div>
        <div className="relative mt-1.5">
          <textarea
            ref={noteRef}
            autoFocus
            value={note}
            onChange={e => setNote(e.target.value)}
            onKeyDown={e => {
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") saveUpdate();
            }}
            // Keyboard-aware: when the on-screen keyboard opens, keep the input
            // (and the Save button just below it) in view within the scroll sheet.
            onFocus={e => {
              const el = e.currentTarget;
              setTimeout(() => el.scrollIntoView?.({ block: "center", behavior: "smooth" }), 250);
            }}
            placeholder={isAr ? "على ماذا عملت؟" : "What did you work on?"}
            rows={3}
            data-testid="capture-note-input"
            className="w-full resize-none rounded-2xl border border-border bg-card p-3 pb-12 text-sm outline-none focus:border-coral"
          />
          {/* Voice is the signature input — a prominent mic docked in the box.
              One tap opens the record → transcribe → review flow for the
              selected day. */}
          <button
            type="button"
            onClick={() => setVoiceOpen(true)}
            aria-label={isAr ? "تسجيل ملاحظة صوتية" : "Record a voice note"}
            data-testid="capture-voice-mic"
            className="absolute bottom-2.5 end-2.5 inline-flex items-center gap-1.5 rounded-full bg-coral px-3 py-1.5 text-xs font-medium text-white shadow-sm shadow-coral/30 transition-transform active:scale-95"
          >
            <Mic className="h-4 w-4" /> {isAr ? "صوت" : "Voice"}
          </button>
        </div>
        <Button
          onClick={saveUpdate}
          disabled={!trimmed || createSignal.isPending}
          className="mt-2 w-full rounded-full shadow-sm shadow-coral/20"
          data-testid="capture-save"
        >
          {createSignal.isPending
            ? isAr ? "جارٍ الحفظ…" : "Saving…"
            : isAr ? "حفظ التحديث" : "Save update"}
        </Button>

        {/* SECONDARY: more ways to capture */}
        <div className="mt-5 text-[11px] font-semibold tracking-wider text-muted-foreground">
          {isAr ? "طرق أخرى للتسجيل" : "MORE WAYS TO CAPTURE"}
        </div>
        <div className="mt-1.5 grid grid-cols-2 gap-2" data-testid="capture-modes">
          {SECONDARY.map(m => {
            const Icon = m.icon;
            return (
              <button
                key={m.label}
                onClick={m.onClick}
                data-testid={`capture-mode-${m.panel ?? m.label.toLowerCase().split(" ")[0]}`}
                className={cn(
                  "flex items-center gap-2 rounded-2xl border px-3 py-2.5 text-sm font-medium",
                  m.active ? "border-coral bg-coral-soft/40 text-coral" : "border-border text-foreground",
                )}
              >
                <Icon className="h-4 w-4 text-coral shrink-0" /> {isAr ? m.labelAr : m.label}
              </button>
            );
          })}
        </div>

        {panel === "blocker" ? (
          <div className="mt-3">
            <BlockerCapture occurredOn={selectedDay} workspaceTeamId={teamId} onLogged={onCaptured} />
          </div>
        ) : null}
        {panel === "priority" ? (
          <div className="mt-3" data-testid="capture-priority">
            <MyPrioritiesCard
              priorities={priorities.data ?? []}
              busyId={setStatus.isPending ? (setStatus.variables as any)?.priorityId : null}
              onCycle={(id, next) => setStatus.mutate({ priorityId: id, status: next })}
            />
          </div>
        ) : null}

        {/* Captured this week — read-only; turning captures into a receipt
            happens in the gated Drafts flow. */}
        <div className="mt-5">
          <CapturedReview
            weekStartKey={weekStartKey}
            teamId={teamId}
            selectedDay={selectedDay}
            onReviewInDrafts={() => {
              onOpenChange(false);
              navigate("/drafts");
            }}
          />
        </div>
      </div>

      {/* Reused capture dialogs. Day-aware via occurredOn, workspace-aware via
          workspaceTeamId — both must match typed capture above, or a capture
          lands in a workspace the user isn't looking at and reads as lost. */}
      <VoiceCaptureDialog open={voiceOpen} onOpenChange={setVoiceOpen} occurredOn={selectedDay} workspaceTeamId={teamId} onLogged={onCaptured} />
      <PhotoCaptureDialog open={photoOpen} onOpenChange={setPhotoOpen} occurredOn={selectedDay} workspaceTeamId={teamId} onLogged={onCaptured} />
    </div>
  );
}
